import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft,
  CalendarCheck,
  MagnifyingGlass,
  Warning,
  CheckCircle,
  PauseCircle,
  PlayCircle,
  Lightning,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  listTuitionPlansWithContext,
  PLAN_HEALTH_REASON_LABELS,
  planStatusLabel,
  runTuitionPlanNow,
  upsertTuitionPlan,
} from "@/lib/tuitionPlans";

/**
 * Operator console for the recurring tuition engine.
 *
 * Shows every tuition plan in the org + derived health signals so an
 * operator can answer "what's about to run / what's paused / what's
 * blocked" at a glance — without leaving this page for 80% of the tasks.
 */
export default function AdminTuitionPlans() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");     // all | active | paused | due | blocked | auto_issue
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listTuitionPlansWithContext();
      setRows(list);
    } catch (err) {
      toast.error(err.message || "Could not load tuition plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => filterRows(rows, { search, filter }), [rows, search, filter]);
  const counts = useMemo(() => countByBucket(rows), [rows]);

  const reload = () => load();

  const onRunNow = async (plan) => {
    if (!window.confirm(`Run plan for ${plan.billing_profile?.family?.display_name || "family"} now?`)) return;
    setBusyId(plan.id);
    try {
      const res = await runTuitionPlanNow(plan.id);
      const row = Array.isArray(res) ? res[0] : res;
      if (!row) toast("Nothing due yet");
      else if (row.status === "created") toast.success("Invoice created");
      else if (row.status === "voided_and_regenerated") toast.success("Regenerated");
      else if (row.reason) toast(`Skipped · ${row.reason}`);
      else toast(`Status: ${row.status || "unknown"}`);
      await reload();
    } catch (err) {
      toast.error(err.message || "Run failed");
    } finally {
      setBusyId(null);
    }
  };

  const onTogglePause = async (plan) => {
    const paused = plan.pause_until && new Date(plan.pause_until) > new Date();
    if (paused) {
      setBusyId(plan.id);
      try {
        await upsertTuitionPlan({
          billingProfileId: plan.billing_profile_id,
          cadence: plan.cadence,
          dayOfMonth: plan.day_of_month,
          autoIssue: plan.auto_issue,
          isActive: true,
          pauseUntil: null,
          notes: plan.notes,
        });
        toast.success("Plan resumed");
        await reload();
      } catch (err) {
        toast.error(err.message || "Could not resume");
      } finally {
        setBusyId(null);
      }
      return;
    }
    const until = window.prompt(
      "Pause until (YYYY-MM-DD). Leave blank to pause indefinitely.",
      new Date(new Date().setMonth(new Date().getMonth() + 1))
        .toISOString()
        .slice(0, 10),
    );
    if (until === null) return;
    setBusyId(plan.id);
    try {
      await upsertTuitionPlan({
        billingProfileId: plan.billing_profile_id,
        cadence: plan.cadence,
        dayOfMonth: plan.day_of_month,
        autoIssue: plan.auto_issue,
        isActive: plan.is_active,
        pauseUntil: until || null,
        notes: plan.notes,
      });
      toast.success(until ? `Paused until ${until}` : "Paused");
      await reload();
    } catch (err) {
      toast.error(err.message || "Could not pause");
    } finally {
      setBusyId(null);
    }
  };

  const onToggleAutoIssue = async (plan) => {
    setBusyId(plan.id);
    try {
      await upsertTuitionPlan({
        billingProfileId: plan.billing_profile_id,
        cadence: plan.cadence,
        dayOfMonth: plan.day_of_month,
        autoIssue: !plan.auto_issue,
        isActive: plan.is_active,
        pauseUntil: plan.pause_until,
        notes: plan.notes,
      });
      toast.success(
        plan.auto_issue
          ? "Auto-issue disabled — drafts stay until admin issues"
          : "Auto-issue enabled",
      );
      await reload();
    } catch (err) {
      toast.error(err.message || "Could not toggle");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-tuition-plans" className="space-y-6">
      <Link
        to="/admin/billing"
        data-testid="plans-back-to-billing"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <CaretLeft size={16} weight="bold" />
        Back to billing
      </Link>

      <section>
        <div className="flex items-center gap-2">
          <CalendarCheck size={22} weight="duotone" className="text-primary-hover" />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Operator console
          </p>
        </div>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Tuition plans
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Every recurring tuition plan across the organization. Use this page
          to confirm what's about to bill, what's paused, and what's blocked
          before the nightly scheduler fires.
        </p>
      </section>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Kpi label="Total plans"    value={counts.total}     testId="plans-kpi-total" />
        <Kpi label="Active"         value={counts.active}    testId="plans-kpi-active"    tone="good" />
        <Kpi label="Paused"         value={counts.paused}    testId="plans-kpi-paused"    tone="warn" />
        <Kpi label="Due ≤ 7 days"   value={counts.dueSoon}   testId="plans-kpi-due-soon"  tone="accent" />
        <Kpi label="Needs attention" value={counts.blocked}  testId="plans-kpi-blocked"   tone="bad" />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2">
            <MagnifyingGlass size={14} weight="duotone" className="text-neutral-400" />
            <input
              data-testid="plans-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search family / email"
              className="w-full bg-transparent text-sm focus:outline-none"
            />
          </div>
          <div className="flex flex-wrap gap-1" data-testid="plans-filter-chips">
            <FilterChip label="All"            value="all"        active={filter} onClick={setFilter} />
            <FilterChip label="Active"         value="active"     active={filter} onClick={setFilter} />
            <FilterChip label="Paused"         value="paused"     active={filter} onClick={setFilter} />
            <FilterChip label="Due ≤ 7 days"   value="due"        active={filter} onClick={setFilter} />
            <FilterChip label="Auto-issue on"  value="auto_issue" active={filter} onClick={setFilter} />
            <FilterChip label="Needs attention" value="blocked"    active={filter} onClick={setFilter} />
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
          <p className="font-heading text-base font-semibold text-neutral-900">
            {rows.length === 0 ? "No tuition plans yet" : "Nothing matches your filter"}
          </p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
            {rows.length === 0
              ? "Plans live on each family's billing page. Pick a family below to set up monthly or weekly tuition."
              : "Try clearing the search box or picking a different chip."}
          </p>
          {rows.length === 0 && (
            <Link
              to="/admin/billing"
              data-testid="plans-empty-create"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
            >
              Pick a family →
            </Link>
          )}
        </div>
      ) : (
        <div
          data-testid="plans-table"
          className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-500">
                <th className="px-4 py-3">Family</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Billing day</th>
                <th className="px-4 py-3">Next run</th>
                <th className="px-4 py-3">Last run</th>
                <th className="px-4 py-3">Health</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((plan) => (
                <PlanRow
                  key={plan.id}
                  plan={plan}
                  busy={busyId === plan.id}
                  onRunNow={() => onRunNow(plan)}
                  onTogglePause={() => onTogglePause(plan)}
                  onToggleAutoIssue={() => onToggleAutoIssue(plan)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function countByBucket(rows) {
  const now = new Date();
  const inSevenDays = new Date(now);
  inSevenDays.setDate(now.getDate() + 7);
  let active = 0, paused = 0, dueSoon = 0, blocked = 0;
  for (const r of rows) {
    const isPaused = r.pause_until && new Date(r.pause_until) > now;
    if (isPaused) paused += 1;
    else if (r.is_active) active += 1;
    const next = r.next_run_on ? new Date(r.next_run_on) : null;
    if (r.is_active && !isPaused && next && next <= inSevenDays) dueSoon += 1;
    if (!r._health?.ok) blocked += 1;
  }
  return { total: rows.length, active, paused, dueSoon, blocked };
}

function filterRows(rows, { search, filter }) {
  const now = new Date();
  const inSevenDays = new Date(now);
  inSevenDays.setDate(now.getDate() + 7);
  const q = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (q) {
      const hay = [
        r.billing_profile?.family?.display_name,
        r.billing_profile?.billing_email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    const isPaused = r.pause_until && new Date(r.pause_until) > now;
    const next = r.next_run_on ? new Date(r.next_run_on) : null;
    if (filter === "active")     return r.is_active && !isPaused;
    if (filter === "paused")     return isPaused;
    if (filter === "due")        return r.is_active && !isPaused && next && next <= inSevenDays;
    if (filter === "auto_issue") return r.auto_issue && r.is_active && !isPaused;
    if (filter === "blocked")    return !r._health?.ok;
    return true;
  });
}

function Kpi({ label, value, testId, tone }) {
  const toneClass =
    tone === "good"   ? "text-primary-hover border-primary/30" :
    tone === "warn"   ? "text-neutral-900 border-secondary/50" :
    tone === "accent" ? "text-primary-hover border-primary/30" :
    tone === "bad"    ? "text-[#C6851E] border-[#C6851E]/40"   :
    "text-neutral-900 border-neutral-200";
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border bg-white p-5 ${toneClass}`}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </p>
      <p className="mt-1 font-heading text-2xl font-semibold tracking-tight">
        {value}
      </p>
    </div>
  );
}

function FilterChip({ label, value, active, onClick }) {
  const isActive = active === value;
  return (
    <button
      type="button"
      data-testid={`plans-filter-${value}`}
      data-active={isActive}
      onClick={() => onClick(value)}
      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
        isActive
          ? "border-primary bg-primary text-white"
          : "border-neutral-200 bg-white text-neutral-700 hover:border-primary/40"
      }`}
    >
      {label}
    </button>
  );
}

function PlanRow({ plan, busy, onRunNow, onTogglePause, onToggleAutoIssue }) {
  const family = plan.billing_profile?.family;
  const paused = plan.pause_until && new Date(plan.pause_until) > new Date();
  const healthy = plan._health?.ok;
  const reasons = plan._health?.reasons || [];

  const nextRun = plan.next_run_on
    ? new Date(plan.next_run_on).toLocaleDateString()
    : "—";
  const lastRun = plan.last_run_at
    ? new Date(plan.last_run_at).toLocaleDateString()
    : "Never";

  return (
    <tr
      data-testid={`plans-row-${plan.id}`}
      data-status={
        paused ? "paused" : plan.is_active ? "active" : "inactive"
      }
      data-health={healthy ? "ok" : "blocked"}
      className="border-t border-neutral-100 align-top"
    >
      <td className="px-4 py-3">
        <Link
          to={`/admin/billing/${family?.id}`}
          data-testid={`plans-row-${plan.id}-family`}
          className="font-semibold text-neutral-900 hover:text-primary-hover"
        >
          {family?.display_name || "Family"}
        </Link>
        <p className="mt-0.5 text-xs text-neutral-500">
          {plan.billing_profile?.billing_email || "—"}
        </p>
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
            paused
              ? "border-neutral-200 bg-neutral-100 text-neutral-600"
              : plan.is_active
                ? "border-primary/30 bg-primary/10 text-primary-hover"
                : "border-neutral-200 bg-neutral-100 text-neutral-500"
          }`}
        >
          {planStatusLabel(plan)}
        </span>
        {plan.auto_issue && (
          <span
            data-testid={`plans-row-${plan.id}-auto-issue`}
            className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-secondary/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-800"
          >
            Auto-issue
          </span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-sm">{plan.day_of_month}</td>
      <td className="px-4 py-3 font-mono text-sm">{nextRun}</td>
      <td className="px-4 py-3">
        <span className="font-mono text-sm">{lastRun}</span>
        {plan.last_result?.status && (
          <p className="mt-0.5 text-[10px] text-neutral-500">
            {plan.last_result.status}
            {plan.last_result.reason ? ` · ${plan.last_result.reason}` : ""}
            {plan.last_result.issued ? " · issued" : ""}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        {healthy ? (
          <span
            data-testid={`plans-row-${plan.id}-health`}
            className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-hover"
          >
            <CheckCircle size={10} weight="bold" />
            Healthy
          </span>
        ) : (
          <div
            data-testid={`plans-row-${plan.id}-health`}
            data-reasons={reasons.join(",")}
            className="flex flex-wrap gap-1"
          >
            {reasons.map((r) => (
              <span
                key={r}
                className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-[#C6851E]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#C6851E]"
              >
                <Warning size={10} weight="bold" />
                {PLAN_HEALTH_REASON_LABELS[r] || r}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap justify-end gap-1">
          <button
            type="button"
            data-testid={`plans-row-${plan.id}-run`}
            onClick={onRunNow}
            disabled={busy}
            title="Run now"
            className="rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            <Lightning size={10} weight="bold" className="mr-1 inline" />
            Run
          </button>
          <button
            type="button"
            data-testid={`plans-row-${plan.id}-pause`}
            onClick={onTogglePause}
            disabled={busy}
            title={paused ? "Resume plan" : "Pause plan"}
            className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-neutral-700 hover:border-neutral-300 disabled:opacity-60"
          >
            {paused ? (
              <><PlayCircle size={10} weight="bold" className="mr-1 inline" /> Resume</>
            ) : (
              <><PauseCircle size={10} weight="bold" className="mr-1 inline" /> Pause</>
            )}
          </button>
          <button
            type="button"
            data-testid={`plans-row-${plan.id}-toggle-auto`}
            onClick={onToggleAutoIssue}
            disabled={busy}
            title="Toggle auto-issue"
            className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-neutral-700 hover:border-neutral-300 disabled:opacity-60"
          >
            {plan.auto_issue ? "Auto-issue on" : "Auto-issue off"}
          </button>
          <Link
            to={`/admin/billing/${family?.id}`}
            data-testid={`plans-row-${plan.id}-open`}
            className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-[11px] font-semibold text-primary-hover hover:bg-primary/5"
          >
            Open
            <CaretRight size={10} weight="bold" />
          </Link>
        </div>
      </td>
    </tr>
  );
}
